import api from './api';

export interface CalendarProvider {
  id: string;
  name: string;
  available: boolean;
  icon: string;
}

export interface CalendarConnection {
  provider: 'google' | 'outlook';
  name: string;
  connected: boolean;
  calendarId?: string;
  lastSync?: string;
  status?: 'idle' | 'syncing' | 'error' | 'disabled';
}

export interface CalendarConnectionsResponse {
  success: boolean;
  connections: CalendarConnection[];
  syncEnabled: boolean;
  lastSync?: string;
  syncStatus?: 'idle' | 'syncing' | 'error' | 'disabled';
  syncError?: string;
  providers: CalendarProvider[];
}

export interface CalendarEvent {
  id: string;
  source: 'google' | 'microsoft';
  title: string;
  start: string;
  end: string;
  isAllDay: boolean;
  status: 'busy' | 'free' | 'tentative';
}

export interface CalendarEventsResponse {
  success: boolean;
  events: CalendarEvent[];
  count: number;
}

export interface GoogleCalendar {
  id: string;
  name: string;
  description?: string;
  primary: boolean;
  accessRole: string;
  backgroundColor?: string;
}

export interface GoogleCalendarsResponse {
  success: boolean;
  calendars: GoogleCalendar[];
  currentCalendarId: string;
  selectedCalendarIds?: string[];
}

export interface SlotHoldResponse {
  success: boolean;
  hold_token: string;
  expires_at: string;
  expires_in_seconds: number;
  slot: {
    start: string;
    end: string;
  };
}

export interface SlotHoldValidation {
  valid: boolean;
  hold_id?: string;
  practitioner_id?: string;
  slot?: {
    start: string;
    end: string;
  };
  expires_at?: string;
  remaining_seconds?: number;
  error?: string;
}

class CalendarService {
  /**
   * Get available calendar providers
   */
  async getProviders(): Promise<CalendarProvider[]> {
    const response = await api.get<{ success: boolean; providers: CalendarProvider[] }>('/calendar/providers');
    return response.data.providers || [];
  }

  /**
   * Get calendar connections for current practitioner
   */
  async getConnections(): Promise<CalendarConnectionsResponse> {
    const response = await api.get<CalendarConnectionsResponse>('/calendar/connections');
    return response.data;
  }

  /**
   * Initiate Google Calendar OAuth flow
   * Returns the OAuth URL to redirect user to
   */
  async initiateGoogleAuth(clinicId?: string): Promise<string> {
    const params = clinicId ? `?clinicId=${clinicId}` : '';
    const response = await api.get<{ success: boolean; authUrl: string }>(`/calendar/google/auth${params}`);
    return response.data.authUrl;
  }

  /**
   * Initiate Outlook Calendar OAuth flow
   * Returns the OAuth URL to redirect user to
   */
  async initiateOutlookAuth(clinicId?: string): Promise<string> {
    const params = clinicId ? `?clinicId=${clinicId}` : '';
    const response = await api.get<{ success: boolean; authUrl: string }>(`/calendar/outlook/auth${params}`);
    return response.data.authUrl;
  }

  /**
   * Disconnect a calendar provider
   */
  async disconnectCalendar(provider: 'google' | 'outlook'): Promise<void> {
    await api.delete(`/calendar/connections/${provider}`);
  }

  /**
   * Trigger manual calendar sync
   */
  async triggerSync(): Promise<{ success: boolean; message: string; result?: any }> {
    const response = await api.post<{ success: boolean; message: string; result?: any }>('/calendar/sync');
    return response.data;
  }

  /**
   * Update calendar sync settings
   */
  async updateSettings(syncEnabled: boolean): Promise<{ success: boolean; message: string; syncEnabled: boolean }> {
    const response = await api.put<{ success: boolean; message: string; syncEnabled: boolean }>('/calendar/settings', {
      syncEnabled,
    });
    return response.data;
  }

  /**
   * Get external calendar events for availability checking
   */
  async getEvents(
    practitionerId?: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<CalendarEventsResponse> {
    const params = new URLSearchParams();
    if (practitionerId) params.append('practitionerId', practitionerId);
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const queryString = params.toString();
    const url = `/calendar/events${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<CalendarEventsResponse>(url);
    return response.data;
  }

  /**
   * Get list of Google calendars for the current practitioner
   */
  async getGoogleCalendars(): Promise<GoogleCalendarsResponse> {
    const response = await api.get<GoogleCalendarsResponse>('/calendar/google/calendars');
    return response.data;
  }

  /**
   * Update Google calendar selection (single - legacy)
   */
  async updateGoogleCalendar(calendarId: string): Promise<{ success: boolean; message: string; calendarId: string }> {
    const response = await api.put<{ success: boolean; message: string; calendarId: string }>('/calendar/google/calendar', {
      calendarId,
    });
    return response.data;
  }

  /**
   * Update Google calendar selection (multiple)
   */
  async updateGoogleCalendars(calendarIds: string[]): Promise<{ success: boolean; message: string; calendarIds: string[] }> {
    const response = await api.put<{ success: boolean; message: string; calendarIds: string[] }>('/calendar/google/calendar', {
      calendarIds,
    });
    return response.data;
  }

  /**
   * Hold a time slot for booking (prevents race conditions)
   */
  async holdSlot(
    clinicId: string,
    practitionerId: string,
    startTime: Date | string,
    duration = 30
  ): Promise<SlotHoldResponse> {
    try {
      const response = await api.post<SlotHoldResponse>(`/emr/${clinicId}/appointments/hold`, {
        practitioner_id: practitionerId,
        start_time: startTime instanceof Date ? startTime.toISOString() : startTime,
        duration,
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409) {
        const errorData = error.response.data;
        throw new Error(errorData?.code || 'SLOT_CONFLICT');
      }
      throw error;
    }
  }

  /**
   * Release a slot hold
   */
  async releaseHold(clinicId: string, holdToken: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/emr/${clinicId}/appointments/hold/${holdToken}`);
    return response.data;
  }

  /**
   * Validate a hold token
   */
  async validateHold(clinicId: string, holdToken: string): Promise<SlotHoldValidation> {
    const response = await api.get<SlotHoldValidation>(`/emr/${clinicId}/appointments/hold/${holdToken}/validate`);
    return response.data;
  }
}

export const calendarService = new CalendarService();
export default calendarService;
